/**
 * The workspace, down the left.
 *
 * The application was four links across the top and a list screen behind each one,
 * which works until somebody has fifty datasets and a hundred documents: then
 * every list is a scroll, nothing can be filed, and finding last month's invoice
 * for one client means reading names. So the navigation *is* the workspace — three
 * trees you can organise, and the pane on the right shows whatever is selected.
 *
 * Three decisions worth their reasons.
 *
 * **Folders are per section.** A folder called "Aldermead" under Data is not the
 * one under Designs. They hold different things and are browsed separately, and
 * merging them would stop a dataset and a design being filed differently for the
 * same client.
 *
 * **Deleting a folder never deletes what is in it.** The service moves the
 * contents up a level and says how much moved; this says so out loud, because a
 * folder that can take a hundred documents with it is not a convenience.
 *
 * **What is open is remembered, per workspace, in this browser.** A tree that
 * collapses on every navigation is a tree you stop using. It is `localStorage`
 * rather than anything on the server: which folders somebody has open is a fact
 * about the machine they are sitting at, and it must survive being unreadable — a
 * private window, cleared site data — without taking the sidebar with it.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, type Brand, type DatasetSummary, type FolderRow, type ReportSummary, type TemplateSummary } from "../api.js";
import { reportFailure } from "../App.js";
import { changed, useChanges } from "../changes.js";
import { go, query, usePath } from "../router.js";
import { Confirm, Icon, Menu, MenuItem, Prompt, bytes, count, toast, when } from "../ui/index.js";
import { useWorkspace } from "../workspace.js";
import { folderHref } from "./Folders.js";
import { buildTree, moveTargets, type FolderItem, type SortBy, type TreeFolder, type TreeItem } from "./tree.js";

type SectionId = "data" | "designs" | "documents";

interface SectionSpec {
  id: SectionId;
  label: string;
  icon: string;
  /** Where the section's own index lives, for the heading's own link. */
  href: string;
  /** Where an item lives. */
  itemHref: (id: string) => string;
  /** What "new" means here, when it is one click. */
  add?: { href: string; label: string };
  /** Other ways of making something new here. */
  more?: Array<{ href: string; label: string }>;
}

const SECTIONS: SectionSpec[] = [
  { id: "data", label: "Your data", icon: "data", href: "/data", itemHref: (id) => `/data/${id}`, add: { href: "/data/new", label: "Bring in data" } },
  { id: "designs", label: "Designs", icon: "design", href: "/designs", itemHref: (id) => `/designs/${id}`, add: { href: "/designs", label: "New design" }, more: [{ href: "/designs/import", label: "Import a Word document or PDF" }] },
  { id: "documents", label: "Documents", icon: "document", href: "/documents", itemHref: () => "/documents" }
];

/** What the sidebar is looking at. Loaded here, so the tree is never a screen's job. */
interface Contents {
  folders: FolderRow[];
  datasets: DatasetSummary[];
  templates: TemplateSummary[];
  reports: ReportSummary[];
  brands: Brand[];
}

const OPEN_KEY = "broadpaper.sidebar.open";
const SORT_KEY = "broadpaper.sidebar.sort";

/** Reads a remembered set, and survives storage being unreadable or nonsense. */
function remembered<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function remember(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // A private window, or site data blocked. Losing which folders were open is
    // not worth a failed interaction.
  }
}

export function Sidebar() {
  const path = usePath();
  // The plan's counters live on the workspace view, and this is the one place that
  // creates and deletes things without navigating — so without this, deleting a
  // design left "you are using all 3 designs your plan includes" on screen until
  // something else happened to reload it.
  const { refresh } = useWorkspace();
  const changes = useChanges();
  const [contents, setContents] = useState<Contents | null>(null);
  const [open, setOpen] = useState<string[]>(() => remembered<string[]>(OPEN_KEY, []));
  const [sort, setSort] = useState<SortBy>(() => remembered<SortBy>(SORT_KEY, "name"));
  const [filter, setFilter] = useState("");
  const [renaming, setRenaming] = useState<{ kind: "folder" | SectionId; id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: "folder" | SectionId; id: string; name: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [folders, datasets, templates, reports, brands] = await Promise.all([
        api.folders(),
        api.datasets(),
        api.templates(),
        api.reports(),
        api.brands()
      ]);
      setContents({
        folders: folders.folders,
        datasets: datasets.datasets,
        templates: templates.templates,
        reports: reports.reports,
        brands: brands.brands
      });
    } catch (e) {
      reportFailure(e, "We couldn't load your workspace.");
    }
  }, []);

  /*
   * Reloaded on every navigation, and whenever anything says the workspace changed.
   *
   * Five small requests per click. Navigation alone missed everything done without
   * leaving the screen — a document deleted from the Documents list stayed in the
   * tree — so the pane's changes reach here through `changed()`, and this tree's
   * changes reach the pane the same way. The previous tree stays on screen while it
   * loads, so nothing flickers.
   */
  useEffect(() => {
    void load();
  }, [load, path, changes]);

  /** After anything this sidebar changes: the tree, the pane beside it and the plan's counts. */
  const reload = useCallback(async () => {
    changed();
    await refresh();
  }, [refresh]);

  useEffect(() => remember(OPEN_KEY, open), [open]);
  useEffect(() => remember(SORT_KEY, sort), [sort]);

  const toggle = (id: string) => setOpen((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const items = useMemo((): Record<SectionId, TreeItem[]> => {
    const c = contents;
    if (!c) return { data: [], designs: [], documents: [] };
    return {
      data: c.datasets.map((d) => ({
        id: d.id,
        name: d.name,
        folderId: d.folderId,
        updatedAt: d.updatedAt,
        note: `${count(d.rowCount)} rows · ${bytes(d.bytes)}`
      })),
      designs: c.templates.map((t) => ({
        id: t.id,
        name: t.name,
        folderId: t.folderId,
        updatedAt: t.updatedAt,
        note: t.dataset ? t.dataset.name : undefined,
        ...(t.dataset ? {} : { badge: { text: "Needs data", tone: "ask" as const } })
      })),
      documents: c.reports.map((r) => ({
        id: r.id,
        name: r.name.replace(/\.pdf$/i, ""),
        folderId: r.folderId,
        updatedAt: r.createdAt,
        note: r.status === "ready" ? `${r.pages ?? "—"} pages · ${when(r.createdAt)}` : undefined,
        ...(r.status === "ready"
          ? {}
          : { badge: { text: r.status === "failed" ? "Failed" : "Being made", tone: r.status === "failed" ? ("bad" as const) : ("ask" as const) } })
      }))
    };
  }, [contents]);

  const current = path.split("?")[0]!.replace(/\/+$/, "") || "/";

  return (
    <nav className="sidebar" aria-label="Your workspace">
      <a className={`sidebar__top${current === "/" ? " is-current" : ""}`} href="/">
        <Icon name="home" className="" /> Home
      </a>

      <div className="sidebar__tools">
        <input
          className="input input--sm"
          placeholder="Search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search your workspace"
        />
        <Menu label="How this is sorted">
          {(close) => (
            <>
              <MenuItem onClick={() => (setSort("name"), close())}>{sort === "name" ? "✓ " : ""}By name</MenuItem>
              <MenuItem onClick={() => (setSort("recent"), close())}>{sort === "recent" ? "✓ " : ""}Most recent first</MenuItem>
            </>
          )}
        </Menu>
      </div>

      {SECTIONS.map((section) => {
        const folders: FolderItem[] = (contents?.folders ?? []).filter((f) => f.section === section.id);
        const tree = buildTree(folders, items[section.id], { sort, filter });
        const targets = moveTargets(folders);
        return (
          <section className="sidebar__section" key={section.id}>
            <div className="sidebar__head">
              <a className={`sidebar__title${current === section.href ? " is-current" : ""}`} href={section.href}>
                <Icon name={section.icon} className="" /> {section.label}
                <span className="sidebar__count">{tree.total}</span>
              </a>
              <Menu label={`${section.label} options`}>
                {(close) => (
                  <>
                    {section.add ? <MenuItem onClick={() => (go(section.add!.href), close())}>{section.add.label}</MenuItem> : null}
                    {(section.more ?? []).map((item) => (
                      <MenuItem key={item.href} onClick={() => (go(item.href), close())}>
                        {item.label}
                      </MenuItem>
                    ))}
                    <MenuItem
                      onClick={() => {
                        close();
                        // Inside the folder the pane is showing, when it is showing one of
                        // this section's: that is where somebody looking at it wants it.
                        const inside = current === section.href ? (query(path).get("folder") ?? "") : "";
                        setRenaming({ kind: "folder", id: `new:${section.id}:${inside}`, name: "" });
                      }}
                    >
                      New folder
                    </MenuItem>
                  </>
                )}
              </Menu>
            </div>

            <div className="sidebar__tree">
              {tree.folders.map((folder) => (
                <FolderNode
                  key={folder.id}
                  folder={folder}
                  section={section}
                  open={open}
                  onToggle={toggle}
                  targets={targets}
                  current={current}
                  onRename={setRenaming}
                  onDelete={setDeleting}
                  onMove={async (kind, id, folderId) => {
                    await move(kind, id, folderId);
                    await reload();
                  }}
                  onNewFolder={(parentId) => setRenaming({ kind: "folder", id: `new:${section.id}:${parentId}`, name: "" })}
                  activeFolder={current === section.href ? query(path).get("folder") : null}
                  folders={folders}
                />
              ))}
              {tree.items.map((item) => (
                <ItemNode
                  key={item.id}
                  item={item}
                  section={section}
                  depth={0}
                  targets={targets}
                  current={current}
                  onRename={setRenaming}
                  onDelete={setDeleting}
                  onMove={async (id, folderId) => {
                    await move(section.id, id, folderId);
                    await reload();
                  }}
                />
              ))}
              {!tree.total ? <p className="sidebar__empty">{filter ? "Nothing matches." : "Nothing here yet."}</p> : null}
            </div>
          </section>
        );
      })}

      <section className="sidebar__section">
        <div className="sidebar__head">
          <a className={`sidebar__title${current === "/brand" ? " is-current" : ""}`} href="/brand">
            <Icon name="settings" className="" /> Your brand
            <span className="sidebar__count">{contents?.brands.length ?? 0}</span>
          </a>
        </div>
        {/* Flat: a workspace holds at most twenty of these and they are all one
            kind of thing, so folders would be scaffolding with nothing in it. */}
        <div className="sidebar__tree">
          {(contents?.brands ?? []).map((brand) => (
            <a key={brand.id} className="sidebar__item" href="/brand" style={{ paddingLeft: 22 }}>
              <span className="sidebar__name">{brand.name}</span>
              {brand.isDefault ? <span className="sidebar__badge">Default</span> : null}
            </a>
          ))}
        </div>
      </section>

      <a className={`sidebar__top sidebar__top--foot${current === "/people" ? " is-current" : ""}`} href="/people">
        <Icon name="people" className="" /> People
      </a>
      <a className={`sidebar__top${current === "/account" ? " is-current" : ""}`} href="/account">
        <Icon name="settings" className="" /> Plan and billing
      </a>

      {renaming ? (
        <Prompt
          title={renaming.id.startsWith("new:") ? "New folder" : `Rename “${renaming.name}”`}
          label="Name"
          initial={renaming.name}
          confirmLabel={renaming.id.startsWith("new:") ? "Create" : "Rename"}
          onCancel={() => setRenaming(null)}
          onConfirm={async (name) => {
            const target = renaming;
            setRenaming(null);
            try {
              if (target.id.startsWith("new:")) {
                const [, section, parentId] = target.id.split(":");
                const created = await api.createFolder({ section: section!, name, parentId: parentId || null });
                setOpen((c) => (parentId && !c.includes(parentId) ? [...c, parentId] : c));
                setOpen((c) => (c.includes(created.folder.id) ? c : [...c, created.folder.id]));
              } else if (target.kind === "folder") {
                await api.updateFolder(target.id, { name });
              } else if (target.kind === "data") {
                await api.updateDataset(target.id, { name });
              } else if (target.kind === "designs") {
                await api.saveTemplate(target.id, { name });
              } else {
                await api.updateReport(target.id, { name });
              }
              await reload();
            } catch (e) {
              reportFailure(e, "We couldn't rename that.");
            }
          }}
        />
      ) : null}

      {deleting ? (
        <Confirm
          title={deleting.kind === "folder" ? `Delete the folder “${deleting.name}”?` : `Delete “${deleting.name}”?`}
          confirmLabel="Delete it"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            const target = deleting;
            setDeleting(null);
            try {
              if (target.kind === "folder") {
                const { itemsMoved, foldersMoved, movedTo } = await api.deleteFolder(target.id);
                const moved = itemsMoved + foldersMoved;
                toast(moved ? `Folder deleted. ${count(moved)} ${moved === 1 ? "thing" : "things"} moved out of it.` : "Folder deleted.", "good");
                // The pane was showing the folder that has gone, so it shows where the
                // contents went instead of an address that no longer means anything.
                const showing = SECTIONS.find((s) => s.href === current);
                if (showing && query(path).get("folder") === target.id) go(folderHref(showing.href, movedTo), { replace: true });
              } else if (target.kind === "data") {
                await api.deleteDataset(target.id);
                toast("Deleted.", "good");
                // The pane was showing, or editing, the set of data that has gone.
                if (current.startsWith(`/data/${target.id}`)) go("/data", { replace: true });
              } else if (target.kind === "designs") {
                await api.deleteTemplate(target.id);
                toast("Deleted.", "good");
              } else {
                // The warning below was shown when the document has live links, so
                // the delete says it knows; without that the service refuses.
                const shared = (contents?.reports.find((r) => r.id === target.id)?.sharing.active ?? 0) > 0;
                await api.deleteReport(target.id, { shared });
                toast(shared ? "Deleted. Its links no longer work." : "Deleted.", "good");
              }
              await reload();
            } catch (e) {
              reportFailure(e, "We couldn't delete that.");
            }
          }}
        >
          {deleting.kind === "folder"
            ? "Nothing inside is deleted — everything in it moves up a level."
            : deleting.kind === "data"
              ? "Any design using it will need data again. Documents already made are not affected."
              : deleting.kind === "designs"
                ? "Its history goes with it. Documents you have already produced from it stay in your Documents, and stay downloadable."
                : (() => {
                    const active = contents?.reports.find((r) => r.id === deleting.id)?.sharing.active ?? 0;
                    return active
                      ? `It has ${active === 1 ? "a share link that still works" : `${active} share links that still work`}. Deleting it stops ${active === 1 ? "that link" : "them"} working, and anybody who opens one is told the document is no longer available. The design it came from is not affected.`
                      : "The file goes for good. The design it came from is not affected.";
                  })()}
        </Confirm>
      ) : null}
    </nav>
  );
}

/**
 * Moving one thing into a folder, or a folder into another.
 *
 * One call per kind because each has its own route, and a folder's parent is a
 * different field from an item's folder — which is worth keeping distinct rather
 * than papering over: the service refuses a folder moved inside its own child, and
 * that check has nowhere to live if both look the same from here.
 */
async function move(kind: SectionId | "folder", id: string, folderId: string | null): Promise<void> {
  if (kind === "folder") await api.updateFolder(id, { parentId: folderId });
  else if (kind === "data") await api.updateDataset(id, { folderId });
  else if (kind === "designs") await api.saveTemplate(id, { folderId });
  else await api.updateReport(id, { folderId });
}

/* --------------------------------- the tree -------------------------------- */

interface NodeProps {
  section: SectionSpec;
  targets: Array<{ id: string; name: string; depth: number }>;
  current: string;
  onRename: (t: { kind: "folder" | SectionId; id: string; name: string }) => void;
  onDelete: (t: { kind: "folder" | SectionId; id: string; name: string }) => void;
}

function FolderNode({
  folder,
  section,
  open,
  onToggle,
  targets,
  current,
  onRename,
  onDelete,
  onMove,
  onNewFolder,
  activeFolder,
  folders
}: NodeProps & {
  folder: TreeFolder;
  /** Every folder in the section, flat, to work out where this one may move. */
  folders: FolderItem[];
  /** The folder the pane is showing, if it is one of this section's. */
  activeFolder: string | null;
  open: string[];
  onToggle: (id: string) => void;
  onMove: (kind: SectionId | "folder", id: string, folderId: string | null) => void | Promise<void>;
  onNewFolder: (parentId: string) => void;
}) {
  const expanded = open.includes(folder.id);
  return (
    <>
      <div className={`sidebar__row${activeFolder === folder.id ? " is-current" : ""}`} style={{ paddingLeft: 6 + folder.depth * 12 }}>
        <button
          type="button"
          className="sidebar__twisty"
          onClick={() => onToggle(folder.id)}
          aria-expanded={expanded}
          aria-label={expanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
        >
          <Icon name="chevron" className={expanded ? "is-open" : ""} />
        </button>
        {/* The name opens the folder in the pane, and opens it in the tree if it was
            shut; the arrow beside it only opens and shuts the tree. */}
        <a className="sidebar__folder" href={folderHref(section.href, folder.id)} title={`Show what is in ${folder.name}`} onClick={() => !expanded && onToggle(folder.id)}>
          <Icon name="folder" className="" />
          <span className="sidebar__name">{folder.name}</span>
          <span className="sidebar__count">{folder.total}</span>
        </a>
        <Menu label={`${folder.name} options`}>
          {(close) => (
            <>
              <MenuItem onClick={() => (close(), onNewFolder(folder.id))}>New folder inside</MenuItem>
              <MenuItem onClick={() => (close(), onRename({ kind: "folder", id: folder.id, name: folder.name }))}>Rename</MenuItem>
              {/* Not `targets`: a folder cannot go inside itself or anything under it,
                  which the service refuses, so none of those is offered. */}
              {moveTargets(folders, folder.id)
                .map((t) => (
                  <MenuItem key={t.id} onClick={() => (close(), void onMove("folder", folder.id, t.id))}>
                    {" ".repeat(t.depth * 2)}Move into {t.name}
                  </MenuItem>
                ))}
              <MenuItem onClick={() => (close(), void onMove("folder", folder.id, null))}>Move to the top</MenuItem>
              <MenuItem tone="bad" onClick={() => (close(), onDelete({ kind: "folder", id: folder.id, name: folder.name }))}>
                Delete folder
              </MenuItem>
            </>
          )}
        </Menu>
      </div>

      {expanded ? (
        <>
          {folder.folders.map((child) => (
            <FolderNode
              key={child.id}
              folder={child}
              section={section}
              open={open}
              onToggle={onToggle}
              targets={targets}
              current={current}
              onRename={onRename}
              onDelete={onDelete}
              onMove={onMove}
              onNewFolder={onNewFolder}
              activeFolder={activeFolder}
              folders={folders}
            />
          ))}
          {folder.items.map((item) => (
            <ItemNode
              key={item.id}
              item={item}
              section={section}
              depth={folder.depth + 1}
              targets={targets}
              current={current}
              onRename={onRename}
              onDelete={onDelete}
              onMove={(id, folderId) => onMove(section.id, id, folderId)}
            />
          ))}
          {!folder.total ? <p className="sidebar__empty" style={{ paddingLeft: 30 + folder.depth * 12 }}>Empty</p> : null}
        </>
      ) : null}
    </>
  );
}


function ItemNode({
  item,
  section,
  depth,
  targets,
  current,
  onRename,
  onDelete,
  onMove
}: NodeProps & { item: TreeItem; depth: number; onMove: (id: string, folderId: string | null) => void | Promise<void> }) {
  const href = section.itemHref(item.id);
  const active = current === href;
  return (
    <div className={`sidebar__row${active ? " is-current" : ""}`} style={{ paddingLeft: 22 + depth * 12 }}>
      <a className="sidebar__item" href={href} title={item.note ? `${item.name} — ${item.note}` : item.name}>
        <span className="sidebar__name">{item.name}</span>
        {item.badge ? <span className={`sidebar__badge sidebar__badge--${item.badge.tone ?? "info"}`}>{item.badge.text}</span> : null}
      </a>
      <Menu label={`${item.name} options`}>
        {(close) => (
          <>
            <MenuItem onClick={() => (close(), onRename({ kind: section.id, id: item.id, name: item.name }))}>Rename</MenuItem>
            {targets.map((t) => (
              <MenuItem key={t.id} onClick={() => (close(), void onMove(item.id, t.id))}>
                {" ".repeat(t.depth * 2)}Move to {t.name}
              </MenuItem>
            ))}
            {item.folderId ? <MenuItem onClick={() => (close(), void onMove(item.id, null))}>Move to the top</MenuItem> : null}
            <MenuItem tone="bad" onClick={() => (close(), onDelete({ kind: section.id, id: item.id, name: item.name }))}>
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
    </div>
  );
}

